import { useState, useEffect, useCallback } from "react";
import { useGenericManager } from "spark-core-dx/hooks";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import _ from "lodash";

export const useDashboardUserNavState = (profileId) => {
    // State Objects and Managers
    const itemServiceFactory = new ItemServiceFactory();
    const userDashNavStateMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.UserDashboardNavState);
    const [userDashNavState, setUserDashNavState] = useState();

    const {
        DashboardTabs: dashboardTabs = [],
        ScrollPosition: scrollPosition = 0,
        SelectedTab: selectedTab = null,
    } = userDashNavState ?? {};

    const [isLoading, setIsLoading] = useState(true);

    // Initialization

    useEffect(() => {
        initUserDashNavState();
    }, [profileId]);

    const initUserDashNavState = async () => {
        if(profileId){
            const result = await userDashNavStateMgr.Query({
                model: "x => x.UserId == profileId",
                params: { profileId },
            });

            if (result?.Success & (result.Items?.length > 0)) {
                setUserDashNavState(result.Items.first());
            } else {
                setUserDashNavState({
                    UserId: profileId,
                    DashboardTabs: [],
                });
            }
            setIsLoading(false);
        }
    };

    const saveAndSetUserDashNavState = async (statePatch) => {
        const newState = { ...userDashNavState, ...statePatch };
        setUserDashNavState(newState);

        try {
            const result = await userDashNavStateMgr.saveItem(newState);
            if (!result.Success) {
                console.error("Failed to save user's dashboard nav state.");
            }
        } catch (error) {
            console.error("Error saving user's dashboard nav state.");
        }
    };

    // Dashboard Tab Modification
    const selectDashboard = (dashboardId) => {
        if (selectedTab !== dashboardId) {
            saveAndSetUserDashNavState({
                SelectedTab: dashboardId,
            });
        }
    };

    const addDashboard = (id) => {
        const newDashboardTabs = [...dashboardTabs, id];

        saveAndSetUserDashNavState({
            DashboardTabs: newDashboardTabs,
            SelectedTab: id,
        });
    };

    const addMultipleDashboards = (ids) => {
        const newDashboardTabs = [...dashboardTabs, ...ids];
        const uniqueDashboardTabs = [...new Set(newDashboardTabs)];

        saveAndSetUserDashNavState({
            DashboardTabs: uniqueDashboardTabs,
            SelectedTab: ids[ids.length - 1],
        });
    };

    const closeDashboard = (id) => {
        const newDashboardTabs = [...dashboardTabs].filter((x) => x !== id);

        saveAndSetUserDashNavState({
            DashboardTabs: newDashboardTabs,
            SelectedTab: selectedTab === id ? newDashboardTabs[0] : selectedTab,
        });
    };

    const closeAllDashboards = () => {
        saveAndSetUserDashNavState({
            DashboardTabs: [],
            SelectedTab: null,
        });
    };

    // Meta Updaters

    // _.debounce creates a function that debounces successive calls to the same function instance.
    // But this is creating a new one every render, so successive calls aren't calling the same instance.
    // To combat this, we are using useCallback :)
    const debouncedSave = useCallback(
        _.debounce((dashNavState) => {
            userDashNavStateMgr?.saveItem(dashNavState);
        }, 5000),
        []
    );

    const updateScrollPosition = async (newScrollPosition) => {
        if (newScrollPosition !== scrollPosition) {
            const newState = {
                ...userDashNavState,
                ScrollPosition: newScrollPosition,
            };

            debouncedSave(newState);
            setUserDashNavState(() => newState);
        }
    };

    const scrollToEndPostDashCreation = (ref) => {
        if (dashboardTabs[dashboardTabs.length - 1] === selectedTab) {
            setTimeout(() => {
                ref.current?.scrollTo({
                    left: ref.current.scrollWidth,
                });
            }, 0);
        }
    };

    return {
        selectDashboard,
        addDashboard,
        addMultipleDashboards,
        closeDashboard,
        closeAllDashboards,
        updateScrollPosition,
        scrollToEndPostDashCreation,
        dashboardTabs,
        scrollPosition,
        selectedTab,
        isLoading
    };
};
